import React, { useEffect, useState } from 'react';
import { G, styles, api, fmtUsd } from '../theme.js';
import { Btn, Badge, Input, Select, Field, Modal, Spinner } from '../ui.jsx';

const TYPES = ['service','part','equipment','membership'];

export function PriceBook({ toast }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [cat, setCat] = useState('all');
  const [importing, setImporting] = useState(null); // { headers, rows, mapping }
  const [dropHot, setDropHot] = useState(false);
  const [viewMode, setViewMode] = useState(() => (typeof window !== 'undefined' && localStorage.getItem('tradeops:pricebook:view')) || 'catalog');
  const [collapsed, setCollapsed] = useState({}); // { 'HVAC': false, 'HVAC/Repairs': false, ... }

  const load = () => api('/api/pricebook').then(setItems).catch(e=>toast(e.message,'error')).finally(()=>setLoading(false));
  useEffect(() => { load(); }, []);
  useEffect(() => { try { localStorage.setItem('tradeops:pricebook:view', viewMode); } catch {} }, [viewMode]);

  const cats = ['all', ...Array.from(new Set(items.map(i=>i.category)))].filter(Boolean);
  const filtered = items
    .filter(i => cat==='all' || i.category===cat || (i.category||'').startsWith(cat+'/'))
    .filter(i => !search || `${i.sku||''} ${i.name} ${i.description||''} ${i.category||''}`.toLowerCase().includes(search.toLowerCase()));

  // ServiceTitan-style grouping: split category on "/" into [trade, subcategory].
  // Items without a "/" land under their category as a flat group.
  const tree = {};
  for (const it of filtered) {
    const parts = (it.category || 'Uncategorized').split('/').map(s => s.trim()).filter(Boolean);
    const top = parts[0] || 'Uncategorized';
    const sub = parts[1] || 'General';
    tree[top] = tree[top] || {};
    tree[top][sub] = tree[top][sub] || [];
    tree[top][sub].push(it);
  }
  const toggleNode = (key) => setCollapsed(c => ({ ...c, [key]: !c[key] }));

  const remove = async i => {
    if (!confirm(`Archive "${i.name}"?`)) return;
    try { await api(`/api/pricebook/${i.id}`, { method:'DELETE' }); load(); toast('Archived'); }
    catch(e) { toast(e.message,'error'); }
  };

  const parseFile = async (file) => {
    if (!file) return;
    const lower = file.name.toLowerCase();
    try {
      if (lower.endsWith('.csv') || file.type === 'text/csv') {
        const text = await file.text();
        const lines = text.split(/\r?\n/).filter(l => l.trim());
        if (lines.length < 2) throw new Error('Empty CSV');
        const parseRow = (line) => {
          const out = []; let cur = ''; let inQ = false;
          for (let i = 0; i < line.length; i++) {
            const c = line[i];
            if (c === '"' && line[i+1] === '"') { cur += '"'; i++; }
            else if (c === '"') inQ = !inQ;
            else if (c === ',' && !inQ) { out.push(cur); cur = ''; }
            else cur += c;
          }
          out.push(cur); return out;
        };
        const headers = parseRow(lines[0]);
        const rows = lines.slice(1).map(l => {
          const cells = parseRow(l);
          return Object.fromEntries(headers.map((h, i) => [h, cells[i] ?? '']));
        });
        const { mapping } = await api('/api/pricebook/suggest-mapping', { method:'POST', body:{ headers, sampleRow: rows[0] }});
        setImporting({ headers, rows, mapping });
      } else if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) {
        // Lazy-load SheetJS from CDN
        if (!window.XLSX) {
          await new Promise((resolve, reject) => {
            const s = document.createElement('script');
            s.src = 'https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js';
            s.onload = resolve; s.onerror = reject;
            document.head.appendChild(s);
          });
        }
        const buf = await file.arrayBuffer();
        const wb = window.XLSX.read(buf, { type:'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = window.XLSX.utils.sheet_to_json(sheet, { defval:'' });
        if (!rows.length) throw new Error('Empty workbook');
        const headers = Object.keys(rows[0]);
        const { mapping } = await api('/api/pricebook/suggest-mapping', { method:'POST', body:{ headers, sampleRow: rows[0] }});
        setImporting({ headers, rows, mapping });
      } else {
        throw new Error('Unsupported file. Use .csv, .xlsx, or .xls');
      }
    } catch (e) { toast(e.message, 'error'); }
  };

  return <div
    onDragOver={(e)=>{ e.preventDefault(); setDropHot(true); }}
    onDragLeave={()=>setDropHot(false)}
    onDrop={(e)=>{ e.preventDefault(); setDropHot(false); const f = e.dataTransfer.files?.[0]; if (f) parseFile(f); }}
    style={{ padding:24, position:'relative' }}>

    {dropHot && (
      <div style={{ position:'fixed', inset:0, background:G.purpleDim+'cc', zIndex:5000, display:'flex', alignItems:'center', justifyContent:'center', pointerEvents:'none' }}>
        <div style={{ ...styles.card, padding:40, textAlign:'center', border:`3px dashed ${G.purple}` }}>
          <div style={{ fontSize:48, marginBottom:10 }}>📥</div>
          <div style={{ fontSize:18, fontWeight:700 }}>Drop your pricebook here</div>
          <div style={{ fontSize:13, color:G.textMuted, marginTop:4 }}>CSV, XLSX, or XLS · we'll map the columns automatically</div>
        </div>
      </div>
    )}

    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
      <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>Price Book <span style={{ color:G.textMuted, fontWeight:500, fontSize:18 }}>({filtered.length})</span></h1>
      <div style={{ display:'flex', gap:8 }}>
        <div style={{ display:'inline-flex', background:G.input, borderRadius:8, padding:2 }}>
          <button onClick={()=>setViewMode('catalog')} style={{ background: viewMode==='catalog'?G.purple:'transparent', color: viewMode==='catalog'?'#fff':G.textMuted, border:'none', padding:'6px 12px', borderRadius:6, fontSize:13, fontWeight:600, cursor:'pointer' }}>Catalog</button>
          <button onClick={()=>setViewMode('table')} style={{ background: viewMode==='table'?G.purple:'transparent', color: viewMode==='table'?'#fff':G.textMuted, border:'none', padding:'6px 12px', borderRadius:6, fontSize:13, fontWeight:600, cursor:'pointer' }}>Table</button>
        </div>
        <CleanupZerosButton toast={toast} onCleaned={load}/>
        <label style={{ ...styles.card, padding:'8px 14px', cursor:'pointer', fontSize:13, display:'inline-flex', alignItems:'center', gap:6 }}>
          Import CSV / Excel
          <input type="file" accept=".csv,.xlsx,.xls,text/csv" style={{ display:'none' }} onChange={(e)=>parseFile(e.target.files?.[0])}/>
        </label>
        <Btn v="primary" onClick={()=>setEditing({ type:'service', unit:'each', taxable:true, active:true })}>+ New Item</Btn>
      </div>
    </div>

    <div style={{ display:'flex', gap:10, marginBottom:14 }}>
      <Input placeholder="Search SKU, name, description, category..." value={search} onChange={e=>setSearch(e.target.value)} style={{ maxWidth:340 }}/>
      <Select value={cat} onChange={e=>setCat(e.target.value)} style={{ maxWidth:240 }}>
        {cats.map(c => <option key={c}>{c}</option>)}
      </Select>
      <div style={{ fontSize:12, color:G.textMuted, alignSelf:'center', marginLeft:'auto' }}>Use "Trade/Subcategory" in the category field to nest (e.g., HVAC/Repairs).</div>
    </div>

    {loading ? <Spinner/> : viewMode === 'catalog' ? (
      // ServiceTitan-style catalog: grouped by Trade → Subcategory → items.
      Object.keys(tree).length === 0 ? (
        <div style={{ padding:48, textAlign:'center', color:G.textMuted, ...styles.card }}>No items match your filters.</div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          {Object.keys(tree).sort().map(top => {
            const subs = tree[top];
            const topCount = Object.values(subs).reduce((s, arr) => s + arr.length, 0);
            const topCollapsed = collapsed[top];
            return (
              <div key={top} style={{ ...styles.card, padding:0, overflow:'hidden' }}>
                <button onClick={()=>toggleNode(top)} style={{ width:'100%', background:G.input, border:'none', padding:'14px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer', textAlign:'left' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <span style={{ fontSize:14, color:G.textMuted, width:14 }}>{topCollapsed?'›':'⌄'}</span>
                    <span style={{ fontSize:17, fontWeight:800, color:G.text }}>{top}</span>
                    <span style={{ fontSize:13, color:G.textMuted }}>{topCount} item{topCount===1?'':'s'}</span>
                  </div>
                  <Btn sz="xs" v="ghost" onClick={(e)=>{ e.stopPropagation(); setEditing({ type:'service', unit:'each', taxable:true, active:true, category: top + '/' }); }}>+ Add to {top}</Btn>
                </button>
                {!topCollapsed && Object.keys(subs).sort().map(sub => {
                  const subKey = `${top}/${sub}`;
                  const subItems = subs[sub];
                  const subCollapsed = collapsed[subKey];
                  return (
                    <div key={sub} style={{ borderTop:`1px solid ${G.cardBorder}` }}>
                      <button onClick={()=>toggleNode(subKey)} style={{ width:'100%', background:'transparent', border:'none', padding:'10px 20px 10px 38px', display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer', textAlign:'left' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <span style={{ fontSize:12, color:G.textMuted, width:10 }}>{subCollapsed?'›':'⌄'}</span>
                          <span style={{ fontSize:14, fontWeight:700, color:G.text }}>{sub}</span>
                          <span style={{ fontSize:12, color:G.textMuted }}>{subItems.length}</span>
                        </div>
                      </button>
                      {!subCollapsed && (
                        <table style={{ width:'100%', borderCollapse:'collapse' }}>
                          <tbody>{subItems.map(i => (
                            <tr key={i.id} onClick={()=>setEditing(i)} style={{ cursor:'pointer', borderTop:`1px solid ${G.cardBorder}` }}>
                              <td style={{ ...styles.td, paddingLeft:38, width:'40%' }}>
                                <div style={{ fontWeight:600 }}>{i.name}</div>
                                <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>{i.sku ? `${i.sku} · ` : ''}{i.type} · {i.unit}{i.description ? ` · ${i.description.slice(0,80)}` : ''}</div>
                              </td>
                              <td style={{ ...styles.td, width:120 }} onClick={e=>e.stopPropagation()}>
                                <div style={{ fontSize:10, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em' }}>Cost</div>
                                <InlineNumber value={i.cost} onSave={async v => { try { await api(`/api/pricebook/${i.id}`, { method:'PATCH', body:{ cost:v }}); load(); toast('Updated'); } catch(e){ toast(e.message,'error'); } }}/>
                              </td>
                              <td style={{ ...styles.td, width:120 }} onClick={e=>e.stopPropagation()}>
                                <div style={{ fontSize:10, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em' }}>Price</div>
                                <div style={{ fontWeight:700 }}>
                                  <InlineNumber value={i.price} onSave={async v => { try { await api(`/api/pricebook/${i.id}`, { method:'PATCH', body:{ price:v }}); load(); toast('Updated'); } catch(e){ toast(e.message,'error'); } }}/>
                                </div>
                              </td>
                              <td style={{ ...styles.td, width:90 }}>
                                <Badge c={i.marginPct>50?G.green:i.marginPct>30?G.yellow:G.red} bg={(i.marginPct>50?G.green:i.marginPct>30?G.yellow:G.red)+'22'}>{i.marginPct}% margin</Badge>
                              </td>
                              <td style={{ ...styles.td, width:40, textAlign:'right' }} onClick={e=>e.stopPropagation()}>
                                <Btn sz="xs" v="danger" onClick={()=>remove(i)}>×</Btn>
                              </td>
                            </tr>
                          ))}</tbody>
                        </table>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )
    ) : (
      <div style={{ ...styles.card, padding:0, overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr>
            {['SKU','Item','Category','Type','Unit','Cost','Price','Margin',''].map(h => <th key={h} style={styles.th}>{h}</th>)}
          </tr></thead>
          <tbody>{filtered.map(i => (
            <tr key={i.id} onClick={()=>setEditing(i)} style={{ cursor:'pointer' }}>
              <td style={{ ...styles.td, fontFamily:'monospace', fontSize:12, color:G.textMuted }}>{i.sku||'—'}</td>
              <td style={styles.td}>
                <div style={{ fontWeight:600 }}>{i.name}</div>
                {i.description && <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>{i.description}</div>}
              </td>
              <td style={styles.td}><Badge c={G.cyan} bg={G.cyanLight}>{i.category}</Badge></td>
              <td style={styles.td}>{i.type}</td>
              <td style={styles.td}>{i.unit}</td>
              <td style={styles.td} onClick={e=>e.stopPropagation()}>
                <InlineNumber value={i.cost} onSave={async v => { try { await api(`/api/pricebook/${i.id}`, { method:'PATCH', body:{ cost:v }}); load(); toast('Updated'); } catch(e){ toast(e.message,'error'); } }}/>
              </td>
              <td style={{ ...styles.td, fontWeight:600 }} onClick={e=>e.stopPropagation()}>
                <InlineNumber value={i.price} onSave={async v => { try { await api(`/api/pricebook/${i.id}`, { method:'PATCH', body:{ price:v }}); load(); toast('Updated'); } catch(e){ toast(e.message,'error'); } }}/>
              </td>
              <td style={styles.td}><Badge c={i.marginPct>50?G.green:i.marginPct>30?G.yellow:G.red} bg={(i.marginPct>50?G.green:i.marginPct>30?G.yellow:G.red)+'22'}>{i.marginPct}%</Badge></td>
              <td style={styles.td} onClick={e=>e.stopPropagation()}><Btn sz="xs" v="danger" onClick={()=>remove(i)}>×</Btn></td>
            </tr>
          ))}</tbody>
        </table>
        {filtered.length === 0 && <div style={{ padding:48, textAlign:'center', color:G.textMuted }}>No items match your filters.</div>}
      </div>
    )}

    <ItemModal item={editing} onClose={()=>setEditing(null)} onSaved={()=>{ load(); setEditing(null); toast('Saved'); }}/>
    <ImportModal data={importing} onClose={()=>setImporting(null)} onDone={(r)=>{
      setImporting(null); load();
      toast(`✓ ${r.created} created · ${r.updated} updated${r.skipped?` · ${r.skipped} skipped`:''}`);
    }} toast={toast}/>
  </div>;
}

const CANONICAL_FIELDS = [
  { key:'name',        label:'Item Name *',  required:true },
  { key:'sku',         label:'SKU' },
  { key:'category',    label:'Category' },
  { key:'unit',        label:'Unit (each, hr, ft)' },
  { key:'price',       label:'Price *',      required:true },
  { key:'cost',        label:'Cost' },
  { key:'description', label:'Description' },
];

function ImportModal({ data, onClose, onDone, toast }) {
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setMapping(data?.mapping || {}); }, [data]);
  if (!data) return null;

  const missing = CANONICAL_FIELDS.filter(f => f.required && !mapping[f.key]);
  const submit = async () => {
    if (missing.length) return toast(`Map required fields: ${missing.map(m=>m.label).join(', ')}`, 'error');
    setBusy(true);
    try {
      const r = await api('/api/pricebook/import', { method:'POST', body:{ rows: data.rows, mapping }});
      onDone(r);
    } catch(e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };

  return <Modal open onClose={onClose} width={680}>
    <h2 style={{ margin:'0 0 6px 0', fontSize:18 }}>📥 Import Pricebook</h2>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:14 }}>
      {data.rows.length} row{data.rows.length===1?'':'s'} detected. We mapped what we could — review and edit any column that's wrong.
    </div>

    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      {CANONICAL_FIELDS.map(f => (
        <Field key={f.key} label={f.label}>
          <Select value={mapping[f.key]||''} onChange={e => setMapping({ ...mapping, [f.key]: e.target.value || undefined })}>
            <option value="">— ignore —</option>
            {data.headers.map(h => <option key={h} value={h}>{h}</option>)}
          </Select>
        </Field>
      ))}
    </div>

    {/* Preview first 3 rows under the mapping they chose */}
    {mapping.name && data.rows[0] && <div style={{ ...styles.card, padding:12, marginTop:14, background:G.input }}>
      <div style={{ fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:8 }}>Preview</div>
      <table style={{ width:'100%', fontSize:12 }}>
        <thead><tr style={{ color:G.textMuted }}>
          {CANONICAL_FIELDS.filter(f => mapping[f.key]).map(f => <th key={f.key} style={{ textAlign:'left', padding:'4px 8px' }}>{f.label.replace(' *','')}</th>)}
        </tr></thead>
        <tbody>{data.rows.slice(0,3).map((r,i) => (
          <tr key={i} style={{ borderTop:`1px solid ${G.border}` }}>
            {CANONICAL_FIELDS.filter(f => mapping[f.key]).map(f => <td key={f.key} style={{ padding:'4px 8px' }}>{r[mapping[f.key]] ?? ''}</td>)}
          </tr>
        ))}</tbody>
      </table>
    </div>}

    <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:18 }}>
      <Btn onClick={onClose} disabled={busy}>Cancel</Btn>
      <Btn v="primary" onClick={submit} disabled={busy || missing.length}>{busy ? <Spinner sz={14}/> : `Import ${data.rows.length} item${data.rows.length===1?'':'s'}`}</Btn>
    </div>
  </Modal>;
}

function ItemModal({ item, onClose, onSaved }) {
  const [form, setForm] = useState({});
  useEffect(() => { setForm(item || {}); }, [item]);
  if (!item) return null;
  const isNew = !form.id;
  const margin = form.price > 0 ? Math.round((form.price - form.cost)/form.price*100) : 0;
  const save = async () => {
    try {
      if (isNew) await api('/api/pricebook', { method:'POST', body: form });
      else await api(`/api/pricebook/${form.id}`, { method:'PATCH', body: form });
      onSaved();
    } catch(e) { alert(e.message); }
  };
  return <Modal open onClose={onClose} width={580}>
    <h2 style={{ margin:'0 0 18px 0' }}>{isNew?'New Price Book Item':form.name}</h2>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <Field label="SKU"><Input value={form.sku||''} onChange={e=>setForm({...form,sku:e.target.value})}/></Field>
      <Field label="Category"><Input value={form.category||''} onChange={e=>setForm({...form,category:e.target.value})} placeholder="HVAC, Parts, Equipment..."/></Field>
      <Field label="Name *" style={{ gridColumn:'1/-1' }}><Input value={form.name||''} onChange={e=>setForm({...form,name:e.target.value})}/></Field>
      <Field label="Type">
        <Select value={form.type||'service'} onChange={e=>setForm({...form,type:e.target.value})}>
          {TYPES.map(t => <option key={t}>{t}</option>)}
        </Select>
      </Field>
      <Field label="Unit"><Input value={form.unit||'each'} onChange={e=>setForm({...form,unit:e.target.value})}/></Field>
      <Field label="Cost ($)"><Input type="number" step="0.01" value={form.cost||0} onChange={e=>setForm({...form,cost:Number(e.target.value)})}/></Field>
      <Field label="Price ($)"><Input type="number" step="0.01" value={form.price||0} onChange={e=>setForm({...form,price:Number(e.target.value)})}/></Field>
      <Field label="Description" style={{ gridColumn:'1/-1' }}><Input value={form.description||''} onChange={e=>setForm({...form,description:e.target.value})}/></Field>
    </div>
    <div style={{ ...styles.card, padding:12, marginTop:10, background:G.input, fontSize:13 }}>
      Margin: <strong style={{ color: margin>50?G.green:margin>30?G.yellow:G.red }}>{margin}%</strong> ({fmtUsd(form.price - form.cost)} per unit)
    </div>

    {(form.type||'service') === 'service' && <BookingRules form={form} setForm={setForm}/>}

    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={save}>Save</Btn>
    </div>
  </Modal>;
}

// Per-service booking rules — when customers can book this service through
// the public booking widget / portal. Empty / null = inherit account defaults.
function BookingRules({ form, setForm }) {
  const b = form.booking || {};
  const setB = (k, v) => setForm({ ...form, booking: { ...(form.booking||{}), [k]: v === '' ? null : Math.max(0, Number(v)||0) }});
  return <div style={{ ...styles.card, padding:14, marginTop:12, borderColor:G.cyan+'66' }}>
    <div style={{ fontWeight:700, marginBottom:6, fontSize:13 }}>📅 Booking Rules for this service</div>
    <div style={{ fontSize:12, color:G.textMuted, lineHeight:1.55, marginBottom:12 }}>
      How customers can self-book this service on your widget / portal. Leave blank to inherit your account defaults (set in Onboarding → Step 3).
    </div>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
      <Field label="Earliest they can book" hint="Minimum lead time in minutes. e.g. 240 = 4 hours notice. 0 = same-minute booking.">
        <Input type="number" min="0" placeholder="e.g. 240" value={b.minLeadMinutes ?? ''} onChange={e=>setB('minLeadMinutes', e.target.value)}/>
      </Field>
      <Field label="Furthest out they can book" hint="Max booking horizon in days. e.g. 60 = 2 months out. 0 = no limit.">
        <Input type="number" min="0" placeholder="e.g. 60" value={b.maxAdvanceDays ?? ''} onChange={e=>setB('maxAdvanceDays', e.target.value)}/>
      </Field>
      <Field label="Buffer after this job (minutes)" hint="Drive / cleanup / paperwork time before the next job can start.">
        <Input type="number" min="0" placeholder="e.g. 30" value={b.bufferMinutes ?? ''} onChange={e=>setB('bufferMinutes', e.target.value)}/>
      </Field>
      <Field label="Default duration (minutes)" hint="Calendar block size. Overrides account default for this service.">
        <Input type="number" min="0" placeholder="e.g. 120" value={b.defaultDurationMinutes ?? ''} onChange={e=>setB('defaultDurationMinutes', e.target.value)}/>
      </Field>
    </div>
  </div>;
}

// Click-to-edit numeric cell. Avoids opening the full ItemModal when the only
// thing the user wants to change is a price or cost.
function InlineNumber({ value, onSave }) {
  const [editing, setEditing] = React.useState(false);
  const [v, setV] = React.useState(value ?? 0);
  React.useEffect(() => { setV(value ?? 0); }, [value]);
  if (!editing) return <span onClick={()=>setEditing(true)} title="Click to edit" style={{ cursor:'text', padding:'2px 6px', borderRadius:4, display:'inline-block' }}>
    {fmtUsd(value)}
  </span>;
  const commit = () => { setEditing(false); if (Number(v) !== Number(value)) onSave(Number(v)||0); };
  return <input
    autoFocus type="number" step="0.01" value={v}
    onChange={e=>setV(e.target.value)}
    onBlur={commit}
    onKeyDown={e=>{ if (e.key==='Enter') commit(); if (e.key==='Escape') { setV(value); setEditing(false); } }}
    style={{ ...styles.inp, width:90, padding:'4px 6px', fontSize:13 }}/>;
}

// Always-visible cleanup button so it's discoverable even before we know the
// count (and even when count is 0 — we still let the user click to confirm
// there's nothing to clean). Previously this returned null when count === 0,
// which made the button invisible right after the user just cleaned it.
function CleanupZerosButton({ toast, onCleaned }) {
  const [count, setCount] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const refresh = () => api('/api/pricebook/zero-count').then(r => setCount(r.count || 0)).catch(() => setCount(0));
  React.useEffect(() => { refresh(); }, []);
  const run = async () => {
    if (count === 0) { toast('No $0-priced items to clean — your price book is already clean.', 'info'); return; }
    if (!confirm(`Permanently delete ${count} price book item${count===1?'':'s'} with $0 prices?\n\nThis is the cleanest fix — they're usually leftover noise from CSV imports. Anything still useful you can re-add manually or re-import.`)) return;
    setBusy(true);
    try {
      const r = await api('/api/pricebook/cleanup-zeros', { method:'POST', body:{ mode:'delete' }});
      toast(`✓ Removed ${r.affected} zero-priced item${r.affected===1?'':'s'}`);
      setCount(0);
      onCleaned?.();
    } catch(e) { toast(e.message, 'error'); } finally { setBusy(false); }
  };
  const label = count === null ? '🧹 Clean $0 items'
    : count === 0 ? '🧹 No $0 items to clean'
    : `🧹 Clean ${count} $0-priced item${count===1?'':'s'}`;
  return <Btn sz="sm" v={count > 0 ? 'warn' : 'ghost'} onClick={run} disabled={busy}>
    {busy ? <Spinner sz={14}/> : label}
  </Btn>;
}
